import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Download, Search, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts`, { params: { search: q, limit: 50 } });
      setContacts(res.data.contacts); setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      setTotal(t => t - 1);
    } catch {}
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/contacts/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'contacts.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div data-testid="contacts-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl font-normal text-gray-900">Contacts <span className="text-sm text-gray-400 font-body font-normal">({total})</span></h2>
        <button onClick={exportCSV} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-indigo-600 transition-colors" data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <form onSubmit={e => { e.preventDefault(); load(search); }} className="mb-5">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="w-full bg-white border border-gray-200 rounded-lg text-gray-900 pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
            data-testid="search-contacts" />
        </div>
      </form>
      {loading ? <p className="text-center py-12 text-gray-400 text-sm">Loading...</p>
      : contacts.length === 0 ? <p className="text-center py-12 text-gray-400 text-sm">No contacts yet. Share your card to start collecting!</p>
      : (
        <div className="space-y-2">
          {contacts.map(c => (
            <div key={c.id} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow group" data-testid={`contact-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900">{c.visitor_name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{c.visitor_phone}{c.visitor_email ? ` \u00b7 ${c.visitor_email}` : ''}{c.visitor_company ? ` \u00b7 ${c.visitor_company}` : ''}</p>
                {c.visitor_notes && <p className="text-xs text-gray-500 mt-1 italic">"{c.visitor_notes}"</p>}
                {c.met_note && <p className="text-[10px] text-gray-400 mt-1">{c.met_note}</p>}
              </div>
              <div className="flex items-center gap-3 ml-3">
                <span className="text-[10px] text-gray-400 font-medium">{new Date(c.created_at).toLocaleDateString()}</span>
                <button onClick={() => deleteContact(c.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all" data-testid={`delete-contact-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
